import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  IconButton,
  Divider,
  Grid,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Paper
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SettingsIcon from '@mui/icons-material/Settings'; // Driven
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'; // Steering
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp'; // Lift
import tireOn from 'assets/images/icons/tireOn.svg';

export const AxleLivePreview = ({ axles = [] }) => {
  const totalAxles = axles?.length || 0;
  const totalTires = (axles || []).reduce((acc, axle) => acc + (axle.is_twin_tire ? 4 : 2), 0);

  return (
    <Box
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        bgcolor: '#f8f9fa',
        borderRadius: 2,
        height: '100%',
        minHeight: 400
      }}
    >
      <Typography variant="overline" sx={{ letterSpacing: 2, color: 'text.secondary', mb: 3, fontWeight: 'bold' }}>
        LIVE LAYOUT PREVIEW
      </Typography>

      {/* SUMMARY STATS */}
      {totalAxles > 0 && (
        <Stack
          direction="row"
          spacing={3}
          sx={{
            mb: 5,
            px: 3,
            py: 1.5,
            bgcolor: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid #f1f5f9',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease'
          }}
        >
          {/* Axles Count */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                bgcolor: '#eff6ff',
                color: '#3b82f6',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'inset 0 0 0 1px rgba(59,130,246,0.1)'
              }}
            >
              <AccountTreeIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ lineHeight: 1.1, fontWeight: 800, color: '#1e293b' }}>
                {totalAxles}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, letterSpacing: 0.5, fontSize: '0.65rem' }}>
                TOTAL AXLES
              </Typography>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ my: 0.5, borderColor: '#e2e8f0', borderStyle: 'dashed' }} />

          {/* Tires Count */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                bgcolor: '#f8fafc',
                color: '#475569',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'inset 0 0 0 1px rgba(71,85,105,0.1)'
              }}
            >
              <Box component="img" src={tireOn} alt="tire" sx={{ width: 18, height: 18, opacity: 0.8 }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ lineHeight: 1.1, fontWeight: 800, color: '#1e293b' }}>
                {totalTires}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, letterSpacing: 0.5, fontSize: '0.65rem' }}>
                TOTAL TIRES
              </Typography>
            </Box>
          </Box>
        </Stack>
      )}

      {!axles || axles.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 10 }}>
          No axles configured.
        </Typography>
      ) : (
        <Box sx={{ position: 'relative', width: 320, display: 'flex', flexDirection: 'column', gap: 7, py: 2, alignItems: 'center' }}>
          {/* Chassis Spine */}
          <Box
            sx={{
              position: 'absolute',
              top: -30,
              bottom: -30,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 12,
              bgcolor: '#94a3b8',
              borderRadius: 6,
              zIndex: 1,
              boxShadow: 'inset 0 0 4px rgba(0,0,0,0.3)'
            }}
          />

          {axles.map((axle, index) => {
            const isLift = axle.is_lift_axle;
            const isTwin = axle.is_twin_tire;
            const isSteering = axle.is_steering;
            const isDriven = axle.is_driver;

            const getTireCode = (axleIndex, twin, pos) => {
              const axleNumber = axleIndex + 1;
              const isFront = axleNumber === 1;
              const rearNumber = axleNumber - 1;
              const rStr = axles.length > 2 ? String(rearNumber) : '';

              if (isFront) {
                if (twin) {
                  if (pos === 'left-outer') return 'FL-OUT';
                  if (pos === 'left-inner') return 'FL-IN';
                  if (pos === 'right-inner') return 'FR-IN';
                  if (pos === 'right-outer') return 'FR-OUT';
                } else {
                  if (pos === 'left-inner') return 'FL';
                  if (pos === 'right-inner') return 'FR';
                }
              } else {
                if (twin) {
                  if (pos === 'left-outer') return `R${rStr}L-OUT`;
                  if (pos === 'left-inner') return `R${rStr}L-IN`;
                  if (pos === 'right-inner') return `R${rStr}R-IN`;
                  if (pos === 'right-outer') return `R${rStr}R-OUT`;
                } else {
                  if (pos === 'left-inner') return `R${rStr}L`;
                  if (pos === 'right-inner') return `R${rStr}R`;
                }
              }
              return '';
            };

            // Render a single tire
            const renderTire = (key) => {
              const code = getTireCode(index, isTwin, key);
              return (
                <Box key={key} sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Box
                    component="img"
                    src={tireOn}
                    alt="tire"
                    sx={{
                      width: 32,
                      height: 64,
                      filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))',
                      transition: 'all 0.3s ease'
                    }}
                  />
                  {code && (
                    <Box
                      sx={{
                        position: 'absolute',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 4,
                        pointerEvents: 'none'
                      }}
                    >
                      {code.split('-').map((part, i) => (
                        <Typography
                          key={i}
                          variant="caption"
                          sx={{
                            color: '#ffffff',
                            fontWeight: 800,
                            fontSize: '0.6rem',
                            textShadow: '0px 0px 4px rgba(0,0,0,1), 0px 0px 2px rgba(0,0,0,1)',
                            textAlign: 'center',
                            lineHeight: 1.1
                          }}
                        >
                          {part}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            };

            return (
              <Box key={index} sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', zIndex: 2 }}>
                {/* Axle Bar */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: 30,
                    right: 30,
                    transform: 'translateY(-50%)',
                    height: 10,
                    bgcolor: '#475569',
                    zIndex: -1,
                    borderRadius: 2,
                    boxShadow: 'inset 0 -2px 4px rgba(0,0,0,0.4)'
                  }}
                />

                {/* Center Indicators */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 3,
                    display: 'flex',
                    gap: 0.5,
                    bgcolor: '#ffffff',
                    px: 1,
                    py: 0.5,
                    borderRadius: 4,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 0.5, color: '#64748b' }}>
                    {index + 1}
                  </Typography>
                  {isSteering && (
                    <Tooltip title="Steering Axle">
                      <SwapHorizIcon sx={{ fontSize: 16, color: '#0ea5e9' }} />
                    </Tooltip>
                  )}
                  {isDriven && (
                    <Tooltip title="Driven Axle">
                      <SettingsIcon sx={{ fontSize: 16, color: '#334155' }} />
                    </Tooltip>
                  )}
                  {isLift && (
                    <Tooltip title="Lift Axle">
                      <KeyboardDoubleArrowUpIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />
                    </Tooltip>
                  )}
                </Box>

                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
                  {/* Left Tires */}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {isTwin && renderTire('left-outer')}
                    {renderTire('left-inner')}
                  </Box>
                  {/* Right Tires */}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {renderTire('right-inner')}
                    {isTwin && renderTire('right-outer')}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export const AxleBuilder = ({ axles = [], setFieldValue }) => {
  // AXLE CONFIGURATION BUILDER
  return (
    <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: '#ffffff' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
          <AccountTreeIcon /> Axle Configuration
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          sx={{
            borderRadius: 4,
            textTransform: 'none',
            px: 2,
            bgcolor: '#f1f5f9',
            color: '#0f172a',
            '&:hover': { bgcolor: '#e2e8f0' },
            boxShadow: 'none'
          }}
          onClick={() => {
            const newAxles = [...(axles || []), { is_steering: false, is_driver: false, is_twin_tire: false, is_lift_axle: false }];
            setFieldValue('axles', newAxles);
          }}
        >
          Add Axle
        </Button>
      </Box>

      <Stack spacing={2}>
        {axles &&
          axles.map((axle, idx) => (
            <Card key={idx} variant="outlined" sx={{ bgcolor: '#f8fafc', borderRadius: 2, borderColor: '#e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 2,
                      bgcolor: '#334155',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 'bold'
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, color: '#334155' }}>
                    Axle {idx + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      const newAxles = [...axles];
                      newAxles.splice(idx, 1);
                      setFieldValue('axles', newAxles);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Divider sx={{ mb: 1.5, borderStyle: 'dashed', borderColor: '#cbd5e1' }} />
                <Grid container>
                  <Grid item size={{ xs: 6, sm: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={axle.is_steering}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFieldValue(`axles[${idx}].is_steering`, checked);
                            if (checked) {
                              setFieldValue(`axles[${idx}].is_twin_tire`, false);
                            }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          Steering
                        </Typography>
                      }
                      sx={{ '& .MuiFormControlLabel-label': { color: '#475569' } }}
                    />
                  </Grid>
                  <Grid item size={{ xs: 6, sm: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={axle.is_driver}
                          onChange={(e) => setFieldValue(`axles[${idx}].is_driver`, e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          Driven
                        </Typography>
                      }
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: '#475569' } }}
                    />
                  </Grid>
                  <Grid item size={{ xs: 6, sm: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={axle.is_twin_tire}
                          disabled={axle.is_steering}
                          onChange={(e) => setFieldValue(`axles[${idx}].is_twin_tire`, e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          Twin Tire
                        </Typography>
                      }
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: '#475569' } }}
                    />
                  </Grid>
                  <Grid item size={{ xs: 6, sm: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={axle.is_lift_axle}
                          onChange={(e) => setFieldValue(`axles[${idx}].is_lift_axle`, e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          Lift Axle
                        </Typography>
                      }
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem', color: '#475569' } }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        {(!axles || axles.length === 0) && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            Click "Add Axle" to start building the axle layout.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};
